import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import supabase from "../../../config/supabaseConfig";
import { useAuth } from "../../../Hooks/useAuth";
import { useGetAds } from "../../../Hooks/useGetAds";
import { Ad } from "../../../types/adType";
import interleaveAds from "../components/balancedShuffleAds";
import { filterActiveAds } from "../../../utils/adFilters";

const MAX_RETRY_ATTEMPTS = 3;
const PAGE_SIZE = 6;

export const useHomeAds = () => {
    const { user } = useAuth();
    const [premiumAds, setPremiumAds] = useState<Ad[]>([]);
    const [verifiedAds, setVerifiedAds] = useState<Ad[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sort, setSort] = useState<string>("");
    const [displayAds, setDisplayAds] = useState<"grid" | "list">("grid");
    const [favorites, setFavorites] = useState<Set<string>>(new Set());
    const [isTogglingFavorite, setIsTogglingFavorite] = useState<Record<string, boolean>>({});
    const [premiumAdsFetchFailed, setPremiumAdsFetchFailed] = useState(false);
    const [verifiedAdsFetchFailed, setVerifiedAdsFetchFailed] = useState(false);
    const [fetchAttempts, setFetchAttempts] = useState({
        premiumAds: 0,
        verifiedAds: 0
    });
    const [activeTab, setActiveTab] = useState<'all' | 'premium' | 'verified'>('all');
    const [currentPage, setCurrentPage] = useState(1);

    const {
        ads: allRegularAds,
        loading: regularAdsLoading,
        fetchFailed,
        retryFetch
    } = useGetAds({
        searchQuery: "",
        sort,
        maxRetries: MAX_RETRY_ATTEMPTS,
        pageSize: 1000
    });

    // Filter out premium ads from regular ads to prevent duplicates
    const regularAds = allRegularAds.filter(ad => !ad.is_premium);

    // Apply filtering to remove sold ads
    const filteredPremiumAds = filterActiveAds(premiumAds);
    const filteredVerifiedAds = filterActiveAds(verifiedAds);
    const filteredRegularAds = filterActiveAds(regularAds);

    const arrangedAllAds = interleaveAds(filteredPremiumAds, filteredRegularAds);
    const paginatedRegularAds = activeTab === 'all' 
        ? arrangedAllAds.slice(
            (currentPage - 1) * PAGE_SIZE,
            currentPage * PAGE_SIZE
        )
        : filteredRegularAds.slice(
            (currentPage - 1) * PAGE_SIZE,
            currentPage * PAGE_SIZE
        );

    const fetchPremiumAds = useCallback(async () => {
        if (fetchAttempts.premiumAds >= MAX_RETRY_ATTEMPTS) {
            setPremiumAdsFetchFailed(true);
            return;
        }

        try {
            const orderColumn = sort === "high" || sort === "low" ? "price" : "published_at";
            const { data, error } = await supabase
                .from("ads")
                .select(`
                    *,
                    categories:category_id(name),
                    subcategories:subcategory_id(name),
                    states:state_id(name),
                    cities:city_id(name),
                    profiles:user_id(id, name, email, is_verified, photo_url),
                    product_images(*)
                `)
                .eq("is_premium", true)
                .eq("is_deleted", false)
                .eq("is_sold", false)
                .order(orderColumn, { ascending: sort === "low" });

            if (error) throw error;
            setPremiumAds((data as unknown as Ad[]) || []);
            setPremiumAdsFetchFailed(false);

        } catch {
            setFetchAttempts(prev => ({
                ...prev,
                premiumAds: prev.premiumAds + 1
            }));

            if (fetchAttempts.premiumAds + 1 >= MAX_RETRY_ATTEMPTS) {
                setPremiumAdsFetchFailed(true);
            }

            message.error("Erreur lors du chargement des annonces premium");
        }
    }, [sort, fetchAttempts.premiumAds]);

    const fetchVerifiedAds = useCallback(async () => {
        if (fetchAttempts.verifiedAds >= MAX_RETRY_ATTEMPTS) {
            setVerifiedAdsFetchFailed(true);
            return;
        }

        try {
            const orderColumn = sort === "high" || sort === "low" ? "price" : "published_at";
            const { data, error } = await supabase
                .from("ads")
                .select(`
                    *,
                    categories:category_id(name),
                    subcategories:subcategory_id(name),
                    states:state_id(name),
                    cities:city_id(name),
                    profiles:user_id!inner(id, name, email, is_verified, photo_url),
                    product_images(*)
                `)
                .eq("profiles.is_verified", true)
                .eq("is_deleted", false)
                .eq("is_sold", false)
                .order(orderColumn, { ascending: sort === "low" });

            if (error) throw error;
            setVerifiedAds((data as unknown as Ad[]) || []);
            setVerifiedAdsFetchFailed(false);

        } catch {
            setFetchAttempts(prev => ({
                ...prev,
                verifiedAds: prev.verifiedAds + 1
            }));

            if (fetchAttempts.verifiedAds + 1 >= MAX_RETRY_ATTEMPTS) {
                setVerifiedAdsFetchFailed(true);
            }

            message.error("Erreur lors du chargement des annonces des vendeurs vérifiés");
        }
    }, [sort, fetchAttempts.verifiedAds]);

    const retryPremiumAdsFetch = useCallback(() => {
        setFetchAttempts(prev => ({
            ...prev,
            premiumAds: 0
        }));
        setPremiumAdsFetchFailed(false);
        fetchPremiumAds();
    }, [fetchPremiumAds]);

    const retryVerifiedAdsFetch = useCallback(() => {
        setFetchAttempts(prev => ({
            ...prev,
            verifiedAds: 0
        }));
        setVerifiedAdsFetchFailed(false);
        fetchVerifiedAds();
    }, [fetchVerifiedAds]);

    const toggleFavorite = async (ad: Ad) => {
        if (!user?.id) {
            message.info("Veuillez vous connecter pour ajouter aux favoris");
            return;
        }

        const adId = ad.id;
        setIsTogglingFavorite(prev => ({ ...prev, [adId]: true }));

        try {
            const isFavorite = favorites.has(adId);

            if (isFavorite) {
                await supabase
                    .from("ad_favorites")
                    .delete()
                    .eq("user_id", user.id)
                    .eq("ad_id", adId);

                setFavorites(prev => {
                    const updated = new Set(prev);
                    updated.delete(adId);
                    return updated;
                });

                message.success("Retiré des favoris");
            } else {
                await supabase
                    .from("ad_favorites")
                    .insert({
                        user_id: user.id,
                        ad_id: adId,
                        created_at: new Date().toISOString()
                    });

                setFavorites(prev => {
                    const updated = new Set(prev);
                    updated.add(adId);
                    return updated;
                });

                message.success("Ajouté aux favoris");
            }
        } catch {
            message.error("Erreur lors de la mise à jour des favoris");
        } finally {
            setIsTogglingFavorite(prev => ({ ...prev, [adId]: false }));
        }
    };

    useEffect(() => {
        const fetchUserFavorites = async () => {
            if (user?.id) {
                try {
                    const { data, error } = await supabase
                        .from("ad_favorites")
                        .select("ad_id")
                        .eq("user_id", user.id);

                    if (error) throw error;

                    const favSet = new Set(data?.map(fav => fav.ad_id) || []);
                    setFavorites(favSet);
                } catch {
                    // Silently handle error - no need to show message for favorites fetch failure
                }
            }
        };

        fetchUserFavorites();
    }, [user]);

    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);
            await Promise.all([fetchPremiumAds(), fetchVerifiedAds()]);
            setIsLoading(false);
        };

        fetchInitialData();
    }, [fetchPremiumAds, fetchVerifiedAds]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, sort]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    return {
        // State
        premiumAds: filteredPremiumAds,
        verifiedAds: filteredVerifiedAds,
        isLoading,
        sort,
        displayAds,
        favorites,
        isTogglingFavorite,
        premiumAdsFetchFailed,
        verifiedAdsFetchFailed,
        activeTab,
        currentPage,
        regularAds: filteredRegularAds,
        regularAdsLoading,
        fetchFailed,
        paginatedRegularAds,
        totalAllAds: arrangedAllAds.length,
        PAGE_SIZE,

        // Actions
        setSort,
        setDisplayAds,
        setActiveTab,
        toggleFavorite,
        handlePageChange,
        retryFetch,
        retryPremiumAdsFetch,
        retryVerifiedAdsFetch
    };
};
